import * as React from 'react';

import { TooltipLabel } from '@/components/ui/TooltipLabel';
import { cn } from '@/lib/utils';

export interface MetricStripItem {
  label: React.ReactNode;
  description?: React.ReactNode;
  value: React.ReactNode;
  meta?: React.ReactNode;
  trend?: {
    value: React.ReactNode;
    tone?: 'positive' | 'negative' | 'neutral';
  };
}

interface MetricStripProps extends React.HTMLAttributes<HTMLDivElement> {
  items: MetricStripItem[];
}

const trendTone = {
  positive: 'text-success',
  negative: 'text-destructive',
  neutral: 'text-muted-foreground',
};

export function MetricStrip({ items, className, ...props }: MetricStripProps) {
  return (
    <div
      className={cn(
        'grid overflow-hidden rounded-lg border border-border/80 bg-card sm:grid-cols-2 lg:grid-cols-4',
        className,
      )}
      {...props}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={cn(
            'flex min-h-[132px] min-w-0 flex-col px-4 py-4 lg:min-h-[160px]',
            index > 0 && 'border-t border-border/70 sm:border-l sm:border-t-0',
            index === 2 && 'sm:border-l-0 lg:border-l',
            index > 1 && 'sm:border-t lg:border-t-0',
          )}
        >
          <div className="flex items-center gap-1.5 text-[13px] font-medium leading-4 text-foreground">
            {item.description ? (
              <TooltipLabel label={item.label} description={item.description} />
            ) : (
              item.label
            )}
          </div>
          <div className="mt-auto flex min-w-0 items-baseline gap-2">
            <div className="truncate text-[24px] font-medium leading-7 tracking-normal text-foreground tabular-nums">
              {item.value}
            </div>
            {item.trend && (
              <div
                className={cn(
                  'shrink-0 text-xs font-medium tabular-nums',
                  trendTone[item.trend.tone ?? 'neutral'],
                )}
              >
                {item.trend.value}
              </div>
            )}
          </div>
          {item.meta && (
            <div className="mt-2 truncate text-[13px] leading-4 text-muted-foreground">
              {item.meta}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
